import { useState, useRef, useEffect, useCallback } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useMessages } from "../hooks/useMessages";
import { T, LANGUAGES } from "../theme";
import Avatar, { colorFromUid } from "../components/Avatar";
import LangBadge from "../components/LangBadge";
import Spinner from "../components/Spinner";
import Waveform from "../components/Waveform";
import AddMemberSheet from "../components/AddMemberSheet";

export default function ChatScreen({ conversationId, currentUser, onBack, addParticipants, renameGroup }) {
  const [convoMeta, setConvoMeta] = useState(null);
  const { messages, loading, loadingEarlier, hasMore, error: messagesError, loadEarlier, sendMessage, sendPhoto, sendVoiceMemo } =
    useMessages(conversationId, convoMeta?.participantLanguages);
  const [metaError, setMetaError] = useState(null);

  const [text, setText] = useState("");
  const [recording, setRecording] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [participantCache, setParticipantCache] = useState({});
  const [quickMode, setQuickMode] = useState(false); // false = GPT-4o double-pass (default)
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [renameSheet, setRenameSheet] = useState(null); // { value } when open
  const [renameSaving, setRenameSaving] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // { id, text, senderName }
  const [contextMenu, setContextMenu] = useState(null); // { msgId, x, y }
  const [sendError, setSendError] = useState(null); // { kind: "photo"|"voice", offline: bool }
  const sendErrorTimerRef = useRef(null);

  const showSendError = useCallback((kind) => {
    if (sendErrorTimerRef.current) clearTimeout(sendErrorTimerRef.current);
    setSendError({ kind, offline: !navigator.onLine });
    sendErrorTimerRef.current = setTimeout(() => setSendError(null), 5000);
  }, []);

  useEffect(() => () => {
    if (sendErrorTimerRef.current) clearTimeout(sendErrorTimerRef.current);
  }, []);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const longPressRef = useRef(null);
  const messageRefs = useRef({}); // id → DOM element for scroll-to
  const scrollContainerRef = useRef(null); // messages scroll container
  const prevScrollHeightRef = useRef(0); // for scroll preservation on load-earlier

  // Load conversation metadata + participant info
  useEffect(() => {
    if (!conversationId) return;
    setMetaError(null);
    (async () => {
      try {
        const convSnap = await getDoc(doc(db, "conversations", conversationId));
        if (!convSnap.exists()) return;
        const data = convSnap.data();
        setConvoMeta(data);

        // Fetch participant profiles
        const cache = {};
        for (const uid of data.participants) {
          if (uid === currentUser.uid) continue;
          const userSnap = await getDoc(doc(db, "users", uid));
          if (userSnap.exists()) {
            cache[uid] = { uid, ...userSnap.data() };
          }
        }
        setParticipantCache(cache);
      } catch (err) {
        // permission-denied (org wall) or network failure — surface it
        // instead of leaving the header/participants silently empty.
        console.error("Conversation metadata load error:", err);
        setMetaError(err);
      }
    })();
  }, [conversationId, currentUser.uid]);

  // Auto-scroll on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, translating]);

  // Header info
  const others = Object.values(participantCache);
  const isGroup = others.length > 1;
  const autoLabel = isGroup
    ? others.map((u) => (u.displayName || "").split(" ")[0]).join(", ")
    : others[0]?.displayName || "Chat";
  const headerLabel = isGroup && convoMeta?.groupName ? convoMeta.groupName : autoLabel;
  const headerSubtitle =
    isGroup && convoMeta?.groupName
      ? `${(convoMeta.participants?.length) || (others.length + 1)} members`
      : null;

  // Send text message
  const sendText = async () => {
    if (!text.trim() || translating) return;
    const t = text.trim();
    const currentReply = replyingTo;
    setText("");
    setReplyingTo(null);
    setTranslating(true);
    try {
      await sendMessage({
        senderUid: currentUser.uid,
        senderLang: currentUser.preferredLanguage,
        text: t,
        quickMode,
        replyTo: currentReply || undefined,
      });
    } catch (err) {
      console.error("Send failed:", err);
    }
    setTranslating(false);
  };

  // Voice recording
  const recStartingRef = useRef(false); // guard against double-starts
  const pendingStopRef = useRef(false); // track if finger lifted before mic ready

  const startRec = useCallback(async () => {
    if (recStartingRef.current || recording) return;
    recStartingRef.current = true;
    pendingStopRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // If finger lifted while waiting for mic permission, abort
      if (pendingStopRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        recStartingRef.current = false;
        return;
      }
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (audioBlob.size > 0) {
          setTranslating(true);
          try {
            await sendVoiceMemo({
              senderUid: currentUser.uid,
              senderLang: currentUser.preferredLanguage,
              audioBlob,
            });
          } catch (err) {
            console.error("Voice memo failed:", err);
            showSendError("voice");
          }
          setTranslating(false);
        }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
      setRecordSecs(0);
      timerRef.current = setInterval(() => setRecordSecs((s) => s + 1), 1000);
    } catch {
      alert("Microphone access required.");
    }
    recStartingRef.current = false;
  }, [currentUser, sendVoiceMemo, recording, showSendError]);

  const stopRec = useCallback(() => {
    // If still waiting for getUserMedia, flag for abort
    if (recStartingRef.current) {
      pendingStopRef.current = true;
    }
    clearInterval(timerRef.current);
    if (mediaRef.current?.state === "recording") mediaRef.current.stop();
    mediaRef.current = null;
    setRecording(false);
    setRecordSecs(0);
  }, []);

  // Photo attach
  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTranslating(true);
    try {
      await sendPhoto({
        senderUid: currentUser.uid,
        senderLang: currentUser.preferredLanguage,
        file,
      });
    } catch (err) {
      console.error("Photo upload failed:", err);
      showSendError("photo");
    }
    setTranslating(false);
    // Reset file input
    if (fileRef.current) fileRef.current.value = "";
  };

  // Format timestamp
  const formatTime = (ts) => {
    if (!ts) return "";
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Long-press handlers for reply
  const handleTouchStart = useCallback((msg, e) => {
    const touch = e.touches[0];
    longPressRef.current = setTimeout(() => {
      // Prevent native iOS callout menu
      e.preventDefault();
      const senderName = msg.senderUid === currentUser.uid
        ? "You"
        : participantCache[msg.senderUid]?.displayName || "Unknown";
      setContextMenu({
        msgId: msg.id,
        x: touch.clientX,
        y: touch.clientY,
        msg,
        senderName,
      });
    }, 500);
  }, [currentUser.uid, participantCache]);

  const handleTouchEnd = useCallback(() => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  }, []);

  const handleTouchMove = useCallback(() => {
    if (longPressRef.current) {
      clearTimeout(longPressRef.current);
      longPressRef.current = null;
    }
  }, []);

  // Dismiss context menu on tap outside
  const dismissMenu = useCallback(() => setContextMenu(null), []);

  // Handle reply selection from context menu
  const handleReply = useCallback(() => {
    if (!contextMenu) return;
    const displayText = contextMenu.msg.photoURL
      ? "Photo"
      : contextMenu.msg.originalText || contextMenu.msg.translatedText || "";
    setReplyingTo({
      id: contextMenu.msg.id,
      text: displayText.substring(0, 80),
      senderName: contextMenu.senderName,
    });
    setContextMenu(null);
  }, [contextMenu]);

  // Handle copy from context menu
  const handleCopy = useCallback(() => {
    if (!contextMenu) return;
    const isMe = contextMenu.msg.senderUid === currentUser.uid;
    const myLang = currentUser.preferredLanguage;
    const copyText = isMe
      ? contextMenu.msg.originalText
      : contextMenu.msg.senderLang === myLang
      ? contextMenu.msg.originalText
      : contextMenu.msg.translatedText || contextMenu.msg.originalText;
    if (copyText) {
      navigator.clipboard.writeText(copyText).catch(() => {});
    }
    setContextMenu(null);
  }, [contextMenu, currentUser]);

  // Load earlier messages with scroll position preservation
  const handleLoadEarlier = useCallback(async () => {
    const container = scrollContainerRef.current;
    if (container) {
      prevScrollHeightRef.current = container.scrollHeight;
    }
    await loadEarlier();
    // After state updates and re-render, restore scroll position
    requestAnimationFrame(() => {
      if (container) {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop += newScrollHeight - prevScrollHeightRef.current;
      }
    });
  }, [loadEarlier]);

  // Scroll to original message when tapping quote
  const scrollToMessage = useCallback((msgId) => {
    const el = messageRefs.current[msgId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.style.transition = "background 0.3s";
      el.style.background = "rgba(255,107,26,0.2)";
      setTimeout(() => {
        el.style.background = "transparent";
      }, 1200);
    }
  }, []);

  const loadError = messagesError || metaError;
  if (loadError) {
    const isPermission = loadError.code === "permission-denied";
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          background: T.bg0,
          padding: 32,
          gap: 12,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 40 }}>{isPermission ? "🔒" : "⚠️"}</div>
        <div style={{ color: T.text, fontWeight: 700, fontSize: 17 }}>
          {isPermission
            ? "You don't have access to this conversation"
            : "Couldn't load this conversation"}
        </div>
        <div style={{ color: T.textDim, fontSize: 14, maxWidth: 300 }}>
          {isPermission
            ? "Ask your team admin to add you, then close and reopen the app. / Pide a tu administrador que te agregue, luego cierra y vuelve a abrir la aplicación."
            : "Check your connection and try again. / Verifica tu conexión e inténtalo de nuevo."}
        </div>
        <button
          onClick={onBack}
          style={{
            marginTop: 8,
            padding: "12px 28px",
            background: T.orange,
            border: "none",
            borderRadius: 8,
            color: "#fff",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          ← Back / Atrás
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          background: T.bg0,
        }}
      >
        <Spinner />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: T.bg0,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "calc(14px + env(safe-area-inset-top, 0px)) 16px 14px",
          borderBottom: `1px solid ${T.border}`,
          background: T.bg1,
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              color: T.orange,
              fontSize: 22,
              padding: 0,
            }}
          >
            ←
          </button>
          <div style={{ position: "relative", flexShrink: 0 }}>
            {others[0] && (
              <Avatar user={others[0]} size={isGroup ? 34 : 42} />
            )}
            {isGroup && others[1] && (
              <div style={{ position: "absolute", bottom: -2, right: -6 }}>
                <Avatar user={others[1]} size={26} />
              </div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              onClick={
                isGroup && renameGroup
                  ? () => setRenameSheet({ value: convoMeta?.groupName || "" })
                  : undefined
              }
              style={{
                fontWeight: 700,
                fontSize: 16,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: isGroup && renameGroup ? "pointer" : "default",
              }}
              title={isGroup && renameGroup ? "Tap to rename group" : undefined}
            >
              {headerLabel}
              {isGroup && renameGroup && (
                <span style={{ marginLeft: 6, color: T.textDim, fontSize: 12, fontWeight: 500 }}>
                  ✎
                </span>
              )}
            </div>
            {headerSubtitle && (
              <div style={{ fontSize: 11, color: T.textDim, marginTop: 1 }}>
                {headerSubtitle}
              </div>
            )}
            <div style={{ fontSize: 11, color: T.textDim, marginTop: 1 }}>
              {isGroup
                ? others.map((u) => (
                    <span key={u.uid} style={{ marginRight: 4 }}>
                      <LangBadge lang={u.preferredLanguage || "en"} />
                    </span>
                  ))
                : others[0] && (
                    <>
                      <LangBadge lang={others[0].preferredLanguage || "en"} />{" "}
                      · Auto-translating
                    </>
                  )}
            </div>
          </div>
          <button
            onClick={() => setShowAddSheet(true)}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "rgba(255,255,255,0.08)",
              border: `1.5px solid ${T.border}`,
              color: T.orange,
              fontSize: 18,
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            title="Add member to chat"
          >
            +
          </button>
          <button
            onClick={() => setQuickMode((prev) => !prev)}
            style={{
              fontSize: 10,
              color: quickMode ? T.textDim : T.orange,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              background: quickMode
                ? "rgba(255,255,255,0.08)"
                : "rgba(255,107,26,0.15)",
              padding: "4px 8px",
              borderRadius: 4,
              border: "none",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            title={
              quickMode
                ? "Quick mode: faster, single-pass (GPT-4o-mini)"
                : "Careful mode: double-pass review (GPT-4o)"
            }
          >
            {quickMode ? "⚡ Quick" : "✓✓ Careful"}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollContainerRef} style={{ flex: 1, overflowY: "auto", padding: "12px 0" }}>
        {/* Load earlier messages button */}
        {hasMore && messages.length > 0 && (
          <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
            <button
              onClick={handleLoadEarlier}
              disabled={loadingEarlier}
              style={{
                background: "rgba(255,255,255,0.06)",
                border: `1px solid ${T.border}`,
                borderRadius: 20,
                padding: "6px 18px",
                color: T.orange,
                fontSize: 12,
                fontWeight: 600,
                fontFamily: "'Barlow', sans-serif",
                cursor: loadingEarlier ? "default" : "pointer",
                opacity: loadingEarlier ? 0.5 : 1,
              }}
            >
              {loadingEarlier ? "Loading..." : "↑ Load earlier messages"}
            </button>
          </div>
        )}
        {messages.length === 0 && (
          <div
            style={{
              textAlign: "center",
              color: T.textMute,
              padding: "60px 30px",
              fontSize: 14,
              lineHeight: 1.7,
            }}
          >
            <div style={{ fontSize: 36, marginBottom: 12 }}>💬</div>
            Start the conversation.
            <br />
            Messages auto-translate for each person.
          </div>
        )}
        {messages.map((msg) => {
          const isMe = msg.senderUid === currentUser.uid;
          const myLang = currentUser.preferredLanguage;

          // Show translated text to the viewer if sender speaks a different language
          // New: check translations map first, fall back to translatedText for old messages
          let displayText = msg.originalText;
          if (!isMe && msg.senderLang !== myLang) {
            displayText = msg.translations?.[myLang] || msg.translatedText || msg.originalText;
          }

          // Show original as subtitle for received messages in another language
          const subText =
            !isMe && msg.senderLang !== myLang ? msg.originalText : null;

          // Bubble color based on sender's language
          const langColor = (lang) =>
            LANGUAGES[lang]?.color || T.enBubble;
          const bubbleColor = isMe
            ? langColor(myLang)
            : langColor(msg.senderLang);

          return (
            <div
              key={msg.id}
              ref={(el) => { messageRefs.current[msg.id] = el; }}
              className="msg-bubble"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: isMe ? "flex-end" : "flex-start",
                padding: "3px 14px",
                animation: "fadeUp 0.2s ease",
                borderRadius: 8,
              }}
              onTouchStart={(e) => handleTouchStart(msg, e)}
              onTouchEnd={handleTouchEnd}
              onTouchMove={handleTouchMove}
            >
              {/* Sender name label (group chat, received messages only) */}
              {isGroup && !isMe && (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: colorFromUid(msg.senderUid),
                    marginBottom: 3,
                    paddingLeft: 4,
                    maxWidth: "76%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {participantCache[msg.senderUid]?.displayName ||
                    participantCache[msg.senderUid]?.username ||
                    "Unknown"}
                </div>
              )}
              {/* Reply quote block */}
              {msg.replyToId && (
                <div
                  onClick={() => scrollToMessage(msg.replyToId)}
                  style={{
                    maxWidth: "76%",
                    background: "rgba(255,255,255,0.06)",
                    borderLeft: `3px solid ${T.orange}`,
                    borderRadius: "6px 8px 8px 6px",
                    padding: "6px 10px",
                    marginBottom: 4,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.orange, marginBottom: 2 }}>
                    {msg.replyToSenderName || "Unknown"}
                  </div>
                  <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.3 }}>
                    {(msg.replyToText || "").substring(0, 60)}
                    {(msg.replyToText || "").length > 60 ? "..." : ""}
                  </div>
                </div>
              )}
              {msg.photoURL ? (
                <div
                  style={{
                    maxWidth: "72%",
                    borderRadius: isMe
                      ? "16px 16px 4px 16px"
                      : "16px 16px 16px 4px",
                    overflow: "hidden",
                    border: `2px solid ${T.border}`,
                  }}
                >
                  <img
                    src={msg.photoURL}
                    alt=""
                    style={{
                      width: "100%",
                      maxWidth: 240,
                      display: "block",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    maxWidth: "76%",
                    background: isMe ? bubbleColor : T.receivedBubble,
                    color: "#fff",
                    borderRadius: isMe
                      ? "18px 18px 4px 18px"
                      : "18px 18px 18px 4px",
                    padding: "11px 15px",
                    fontSize: 17,
                    lineHeight: 1.45,
                    fontWeight: 500,
                  }}
                >
                  {msg.isAudio && (
                    <span style={{ marginRight: 6, opacity: 0.8 }}>🎙</span>
                  )}
                  {displayText}
                </div>
              )}
              {subText && (
                <div
                  style={{
                    fontSize: 11,
                    color: T.textMute,
                    marginTop: 3,
                    maxWidth: "76%",
                    fontStyle: "italic",
                    paddingLeft: 4,
                  }}
                >
                  {subText}
                </div>
              )}
              <div
                style={{
                  fontSize: 10,
                  color: T.textMute,
                  marginTop: 3,
                  paddingLeft: 4,
                  paddingRight: 4,
                }}
              >
                {formatTime(msg.createdAt)}
                {isMe && (
                  <span style={{ marginLeft: 6, color: T.orange }}>✓✓</span>
                )}
              </div>
            </div>
          );
        })}

        {translating && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 14px",
            }}
          >
            <div
              style={{
                background: T.bg2,
                borderRadius: "18px 18px 18px 4px",
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <Spinner />
              <span style={{ fontSize: 12, color: T.textDim }}>
                Translating...
              </span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Recording overlay */}
      {recording && (
        <div
          style={{
            background: "rgba(0,0,0,0.85)",
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "16px 0",
            gap: 10,
            borderTop: `1px solid ${T.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: T.danger,
                animation: "blink 1s infinite",
              }}
            />
            <span
              style={{
                color: T.danger,
                fontWeight: 700,
                fontSize: 13,
                letterSpacing: 1,
              }}
            >
              RECORDING {recordSecs}s
            </span>
          </div>
          <Waveform bars={9} />
          <span style={{ fontSize: 11, color: T.textDim }}>
            Tap mic to send
          </span>
        </div>
      )}

      {/* Context menu overlay */}
      {contextMenu && (
        <div
          onClick={dismissMenu}
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 100,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              left: Math.min(contextMenu.x, window.innerWidth - 140),
              top: Math.max(contextMenu.y - 50, 10),
              background: T.bg2,
              border: `1.5px solid ${T.border}`,
              borderRadius: 12,
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              overflow: "hidden",
              zIndex: 101,
              minWidth: 120,
            }}
          >
            <button
              onClick={handleReply}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                padding: "14px 18px",
                background: "none",
                border: "none",
                color: T.text,
                fontSize: 15,
                fontWeight: 600,
                fontFamily: "'Barlow', sans-serif",
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 18 }}>↩</span> Reply
            </button>
            <div style={{ height: 1, background: T.border, margin: "0 12px" }} />
            <button
              onClick={handleCopy}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                width: "100%",
                padding: "14px 18px",
                background: "none",
                border: "none",
                color: T.text,
                fontSize: 15,
                fontWeight: 600,
                fontFamily: "'Barlow', sans-serif",
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 18 }}>📋</span> Copy
            </button>
          </div>
        </div>
      )}

      {/* Input bar */}
      <div
        style={{
          borderTop: `1px solid ${T.border}`,
          background: T.bg1,
          flexShrink: 0,
        }}
      >
        {/* Send-error toast (offline photo/voice) */}
        {sendError && (
          <div
            role="alert"
            onClick={() => setSendError(null)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 14px",
              background: "rgba(239,68,68,0.15)",
              borderBottom: `1px solid ${T.danger}`,
              color: T.text,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 16 }}>⚠️</span>
            <span style={{ flex: 1 }}>
              {sendError.offline
                ? `You're offline — ${sendError.kind === "photo" ? "photos" : "voice memos"} can't send until you reconnect.`
                : `${sendError.kind === "photo" ? "Photo" : "Voice memo"} failed to send. Tap to dismiss and try again.`}
            </span>
            <span style={{ color: T.textDim, fontSize: 14 }}>✕</span>
          </div>
        )}
        {/* Reply preview bar */}
        {replyingTo && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              padding: "8px 14px",
              borderBottom: `1px solid ${T.border}`,
              gap: 10,
            }}
          >
            <div
              style={{
                flex: 1,
                borderLeft: `3px solid ${T.orange}`,
                paddingLeft: 10,
                minWidth: 0,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: T.orange }}>
                {replyingTo.senderName}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: T.textDim,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {replyingTo.text}
              </div>
            </div>
            <button
              onClick={() => setReplyingTo(null)}
              style={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                border: "none",
                color: T.textDim,
                fontSize: 16,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>
        )}
        <div
          style={{
            padding: "10px 12px calc(14px + env(safe-area-inset-bottom, 0px))",
            display: "flex",
            alignItems: "flex-end",
            gap: 8,
          }}
        >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handlePhoto}
        />
        <button
          onClick={() => fileRef.current?.click()}
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            background: T.bg2,
            border: `1.5px solid ${T.border}`,
            color: T.textDim,
            fontSize: 20,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          📷
        </button>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendText();
            }
          }}
          placeholder={`Message in ${
            currentUser.preferredLanguage === "en" ? "English" : "Español"
          }...`}
          rows={1}
          style={{
            flex: 1,
            padding: "11px 14px",
            background: T.bg2,
            border: `1.5px solid ${T.border}`,
            borderRadius: 10,
            color: T.text,
            fontSize: 17,
            resize: "none",
            outline: "none",
            lineHeight: 1.4,
            maxHeight: 100,
            overflowY: "auto",
            fontFamily: "'Barlow', sans-serif",
          }}
        />

        {text.trim() ? (
          <button
            onClick={sendText}
            disabled={translating}
            style={{
              width: 52,
              height: 44,
              borderRadius: 10,
              background: T.orange,
              border: "none",
              color: "#fff",
              fontSize: 18,
              fontWeight: 700,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: translating ? 0.5 : 1,
            }}
          >
            ↑
          </button>
        ) : (
          <button
            onClick={() => { recording ? stopRec() : startRec(); }}
            disabled={translating}
            style={{
              width: 64,
              height: 64,
              borderRadius: 14,
              background: recording ? T.danger : T.orange,
              border: "none",
              color: "#fff",
              fontSize: 28,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: recording ? "pulse 1s infinite" : "none",
              boxShadow: recording
                ? "0 0 0 4px rgba(239,68,68,0.3)"
                : "0 4px 16px rgba(255,107,26,0.4)",
              transition: "all 0.15s",
            }}
          >
            {recording ? <Waveform bars={4} /> : "🎙"}
          </button>
        )}
        </div>
      </div>

      {/* Add member sheet */}
      {showAddSheet && (
        <AddMemberSheet
          currentUser={currentUser}
          existingParticipants={convoMeta?.participants || []}
          onAdd={async (newUsers) => {
            await addParticipants(conversationId, newUsers);
            // Refresh participant cache with new members
            const cache = { ...participantCache };
            for (const u of newUsers) {
              const userSnap = await getDoc(doc(db, "users", u.uid));
              if (userSnap.exists()) {
                cache[u.uid] = { uid: u.uid, ...userSnap.data() };
              }
            }
            setParticipantCache(cache);
            // Also update local convoMeta
            setConvoMeta((prev) => ({
              ...prev,
              participants: [
                ...prev.participants,
                ...newUsers.map((u) => u.uid),
              ],
              participantLanguages: {
                ...prev.participantLanguages,
                ...Object.fromEntries(
                  newUsers.map((u) => [u.uid, u.preferredLanguage || "en"])
                ),
              },
            }));
          }}
          onClose={() => setShowAddSheet(false)}
        />
      )}

      {/* Rename group sheet */}
      {renameSheet && (
        <div
          onClick={() => !renameSaving && setRenameSheet(null)}
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 200,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: T.bg1,
              border: `1.5px solid ${T.border}`,
              borderRadius: 14,
              padding: 20,
              width: "100%",
              maxWidth: 340,
              display: "flex",
              flexDirection: "column",
              gap: 14,
            }}
          >
            <div style={{ fontSize: 17, fontWeight: 700, color: T.text }}>
              Rename group
            </div>
            <input
              autoFocus
              value={renameSheet.value}
              onChange={(e) => setRenameSheet({ value: e.target.value })}
              placeholder="Group name"
              maxLength={60}
              style={{
                padding: "11px 14px",
                background: T.bg2,
                border: `1.5px solid ${T.border}`,
                borderRadius: 10,
                color: T.text,
                fontSize: 16,
                outline: "none",
                fontFamily: "'Barlow', sans-serif",
              }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setRenameSheet(null)}
                disabled={renameSaving}
                style={{
                  padding: "10px 18px",
                  background: "rgba(255,255,255,0.08)",
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  color: T.textDim,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const trimmed = renameSheet.value.trim();
                  if (!trimmed) return;
                  setRenameSaving(true);
                  try {
                    await renameGroup(conversationId, trimmed);
                    setConvoMeta((prev) => ({ ...prev, groupName: trimmed }));
                    setRenameSheet(null);
                  } catch (err) {
                    console.error("Rename failed:", err);
                    alert("Failed to rename group. Try again.");
                  }
                  setRenameSaving(false);
                }}
                disabled={renameSaving || !renameSheet.value.trim()}
                style={{
                  padding: "10px 18px",
                  background: T.orange,
                  border: "none",
                  borderRadius: 8,
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  opacity: renameSaving || !renameSheet.value.trim() ? 0.5 : 1,
                }}
              >
                {renameSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
